// Player-facing visual settings and the per-tier quality profiles they select (docs/ART-DIRECTION.md §4,
// §5, §7).

import type { PaletteMode } from '@taxwar/tuning'

export type QualityTier = 'low' | 'medium' | 'high'

export interface VisualSettings {
  readonly quality: QualityTier
  /** ART-DIRECTION §4: disables every post effect except bloom (scanlines, aberration, vignette, flicker). */
  readonly reduceEffects: boolean
  /** ART-DIRECTION §3: the normal palette, or the colour-blind one (it overrides a few roles, @taxwar/tuning palette.ts). */
  readonly palette: PaletteMode
}

export interface QualityProfile {
  /** Cap on window.devicePixelRatio for the single renderer. */
  readonly maxPixelRatio: number
  /** MSAA samples on the post-processing render targets (0 = off). */
  readonly msaaSamples: number
  readonly sphereWidthSegments: number
  readonly sphereHeightSegments: number
  /** Land point-cloud density relative to High (ART-DIRECTION §7: ×0.5 on Low). */
  readonly landPointDensity: number
}

export const QUALITY_PROFILES: Readonly<Record<QualityTier, QualityProfile>> = {
  low: { maxPixelRatio: 1, msaaSamples: 0, sphereWidthSegments: 48, sphereHeightSegments: 32, landPointDensity: 0.5 },
  medium: { maxPixelRatio: 1.5, msaaSamples: 2, sphereWidthSegments: 72, sphereHeightSegments: 48, landPointDensity: 0.75 },
  high: { maxPixelRatio: 2, msaaSamples: 4, sphereWidthSegments: 96, sphereHeightSegments: 64, landPointDensity: 1 },
}

// ART-DIRECTION §5 targets low-end laptops; High keeps three HalfFloat MSAA targets and is opt-in.
const DEFAULT_QUALITY: QualityTier = 'medium'

/** Starting settings; "Reduce effects" follows the OS reduced-motion preference until the player changes it. */
export function initialVisualSettings(prefersReducedMotion: boolean): VisualSettings {
  return { quality: DEFAULT_QUALITY, reduceEffects: prefersReducedMotion, palette: 'normal' }
}
